import {
  Bold,
  BookText,
  Code,
  Heading1,
  Heading2,
  Italic,
  List,
  ListOrdered,
  Quote,
} from 'lucide-react'
import { Editor, Element as SlateElement, Transforms } from 'slate'
import { useSlate } from 'slate-react'

import { Separator } from '@/components/ui/separator'

import type { CustomElement } from '../../types/slate'

import { ToolbarButton } from './ToolbarButton'

const MARK_BOLD = 'bold'
const MARK_ITALIC = 'italic'
const MARK_CODE = 'code'
const ELEMENT_HEADING = 'heading'
const ELEMENT_BLOCK_QUOTE = 'block-quote'
const ELEMENT_BULLETED_LIST = 'bulleted-list'
const ELEMENT_NUMBERED_LIST = 'numbered-list'
const ELEMENT_LIST_ITEM = 'list-item'
const ELEMENT_PARAGRAPH = 'paragraph'
const HEADING_LEVEL_ONE = 1
const HEADING_LEVEL_TWO = 2

export const DEFAULT_TOOLBAR_ICON_SIZE = 18

const ARIA_LABEL_BOLD = 'Bold (Cmd+B)'
const ARIA_LABEL_ITALIC = 'Italic (Cmd+I)'
const ARIA_LABEL_CODE = 'Code'
const ARIA_LABEL_HEADING_ONE = 'Heading 1'
const ARIA_LABEL_HEADING_TWO = 'Heading 2'
const ARIA_LABEL_BLOCK_QUOTE = 'Block Quote'
const ARIA_LABEL_BULLETED_LIST = 'Bulleted List'
const ARIA_LABEL_NUMBERED_LIST = 'Numbered List'

export interface EditorToolbarProps {
  disabled?: boolean
  mode?: 'slate' | 'markdown'
  onMarkdownInsert?: (syntax: string, wrapText?: boolean) => void
  toolbarIconSize?: number
  onTemplateClick?: () => void
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  disabled = false,
  mode = 'slate',
  onMarkdownInsert,
  toolbarIconSize = DEFAULT_TOOLBAR_ICON_SIZE,
  onTemplateClick,
}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const editor = mode === 'slate' ? useSlate() : null

  const isMarkActive = (mark: string) => {
    if (!editor) return false
    try {
      const marks = Editor.marks(editor)
      return marks ? marks[mark as keyof typeof marks] === true : false
    } catch {
      return false
    }
  }

  const isBlockActive = (blockType: string) => {
    if (!editor) return false
    const { selection } = editor
    if (!selection) return false

    try {
      const [match] = Array.from(
        Editor.nodes(editor, {
          at: Editor.unhangRange(editor, selection),
          match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === blockType,
        })
      )

      return !!match
    } catch {
      return false
    }
  }

  const isHeadingLevel = (level: number) => {
    if (!editor) return false
    const { selection } = editor
    if (!selection) return false

    try {
      const [match] = Array.from(
        Editor.nodes(editor, {
          at: Editor.unhangRange(editor, selection),
          match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === ELEMENT_HEADING &&
            (n as CustomElement & { level?: number }).level === level,
        })
      )

      return !!match
    } catch {
      return false
    }
  }

  const toggleMark = (mark: string) => {
    if (mode === 'markdown' && onMarkdownInsert) {
      // Handle markdown mode
      const syntaxMap: Record<string, string> = {
        [MARK_BOLD]: '**',
        [MARK_ITALIC]: '*',
        [MARK_CODE]: '`',
      }
      const syntax = syntaxMap[mark]
      if (syntax) {
        onMarkdownInsert(syntax, true)
      }
      return
    }

    if (!editor) return
    const isActive = isMarkActive(mark)
    if (isActive) {
      Editor.removeMark(editor, mark)
    } else {
      Editor.addMark(editor, mark, true)
    }
  }

  const toggleBlock = (blockType: string) => {
    if (mode === 'markdown' && onMarkdownInsert) {
      // Handle markdown mode
      const syntaxMap: Record<string, { syntax: string; wrap: boolean }> = {
        [ELEMENT_HEADING]: { syntax: '# ', wrap: false },
        [ELEMENT_BLOCK_QUOTE]: { syntax: '> ', wrap: false },
        [ELEMENT_BULLETED_LIST]: { syntax: '- ', wrap: false },
        [ELEMENT_NUMBERED_LIST]: { syntax: '1. ', wrap: false },
      }
      const config = syntaxMap[blockType]
      if (config) {
        onMarkdownInsert(config.syntax, config.wrap)
      }
      return
    }

    if (!editor) return
    const isActive = isBlockActive(blockType)
    const isList =
      blockType === ELEMENT_BULLETED_LIST || blockType === ELEMENT_NUMBERED_LIST

    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n.type === ELEMENT_BULLETED_LIST || n.type === ELEMENT_NUMBERED_LIST),
      split: true,
    })

    let newType: CustomElement['type']
    if (isActive) {
      newType = ELEMENT_PARAGRAPH
    } else if (isList) {
      newType = ELEMENT_LIST_ITEM
    } else {
      newType = blockType as CustomElement['type']
    }

    const newProperties: Partial<SlateElement> = {
      type: newType,
    }

    Transforms.setNodes<SlateElement>(editor, newProperties)

    if (!isActive && isList) {
      const block =
        blockType === ELEMENT_BULLETED_LIST
          ? ({ type: ELEMENT_BULLETED_LIST, children: [] } as CustomElement)
          : ({ type: ELEMENT_NUMBERED_LIST, children: [] } as CustomElement)
      Transforms.wrapNodes(editor, block)
    }
  }

  const toggleHeading = (level: number) => {
    if (mode === 'markdown' && onMarkdownInsert) {
      const syntax = '#'.repeat(level) + ' '
      onMarkdownInsert(syntax, false)
      return
    }

    if (!editor) return
    const isActive = isBlockActive(ELEMENT_HEADING)
    Transforms.setNodes<SlateElement>(
      editor,
      {
        type: isActive ? ELEMENT_PARAGRAPH : ELEMENT_HEADING,
        level,
      } as Partial<SlateElement>,
      { match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) }
    )
  }

  const toggleBlockQuote = () => toggleBlock(ELEMENT_BLOCK_QUOTE)
  const toggleBold = () => toggleMark(MARK_BOLD)
  const toggleItalic = () => toggleMark(MARK_ITALIC)
  const toggleCode = () => toggleMark(MARK_CODE)
  const toggleBulletedList = () => toggleBlock(ELEMENT_BULLETED_LIST)
  const toggleNumberedList = () => toggleBlock(ELEMENT_NUMBERED_LIST)

  return (
    <>
      <div className="flex rounded-t-xl h-14 p-2 items-center gap-1 flex-[0_0_2.25rem] overflow-y-hidden overflow-x-auto">
        <ToolbarButton
          icon={<Bold size={toolbarIconSize} />}
          onClick={toggleBold}
          isActive={isMarkActive(MARK_BOLD)}
          ariaLabel={ARIA_LABEL_BOLD}
          title={ARIA_LABEL_BOLD}
          disabled={disabled}
        />

        <ToolbarButton
          icon={<Italic size={toolbarIconSize} />}
          onClick={toggleItalic}
          isActive={isMarkActive(MARK_ITALIC)}
          ariaLabel={ARIA_LABEL_ITALIC}
          title={ARIA_LABEL_ITALIC}
          disabled={disabled}
        />

        <ToolbarButton
          icon={<Code size={toolbarIconSize} />}
          onClick={toggleCode}
          isActive={isMarkActive(MARK_CODE)}
          ariaLabel={ARIA_LABEL_CODE}
          title={ARIA_LABEL_CODE}
          disabled={disabled}
        />

        <ToolbarButton
          icon={<Heading1 size={toolbarIconSize} />}
          onClick={() => toggleHeading(HEADING_LEVEL_ONE)}
          isActive={isHeadingLevel(HEADING_LEVEL_ONE)}
          ariaLabel={ARIA_LABEL_HEADING_ONE}
          title={ARIA_LABEL_HEADING_ONE}
          disabled={disabled}
        />

        <ToolbarButton
          icon={<Heading2 size={toolbarIconSize} />}
          onClick={() => toggleHeading(HEADING_LEVEL_TWO)}
          isActive={isHeadingLevel(HEADING_LEVEL_TWO)}
          ariaLabel={ARIA_LABEL_HEADING_TWO}
          title={ARIA_LABEL_HEADING_TWO}
          disabled={disabled}
        />

        <ToolbarButton
          icon={<Quote size={toolbarIconSize} />}
          onClick={toggleBlockQuote}
          isActive={isBlockActive(ELEMENT_BLOCK_QUOTE)}
          ariaLabel={ARIA_LABEL_BLOCK_QUOTE}
          title={ARIA_LABEL_BLOCK_QUOTE}
          disabled={disabled}
        />

        <ToolbarButton
          icon={<ListOrdered size={toolbarIconSize} />}
          onClick={toggleNumberedList}
          isActive={isBlockActive(ELEMENT_NUMBERED_LIST)}
          ariaLabel={ARIA_LABEL_NUMBERED_LIST}
          title={ARIA_LABEL_NUMBERED_LIST}
          disabled={disabled}
        />

        <ToolbarButton
          icon={<List size={toolbarIconSize} />}
          onClick={toggleBulletedList}
          isActive={isBlockActive(ELEMENT_BULLETED_LIST)}
          ariaLabel={ARIA_LABEL_BULLETED_LIST}
          title={ARIA_LABEL_BULLETED_LIST}
          disabled={disabled}
        />

        {onTemplateClick && (
          <ToolbarButton
            icon={<BookText size={toolbarIconSize} />}
            onClick={onTemplateClick}
            isActive={false}
            ariaLabel="Comment Template"
            title="Comment Template"
            disabled={disabled}
          />
        )}
      </div>
      <Separator className="flex-[0_0_1px]" />
    </>
  )
}
